import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post } from "@/lib/api";
import { resetDayStatus } from "@/lib/dayStatus";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

export function AppProvider({ children }) {
  const qc = useQueryClient();
  const [theme, setThemeState] = useState(() => localStorage.getItem("iris-theme") || "light");

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    localStorage.setItem("iris-theme", theme);
  }, [theme]);

  const toggleTheme = () => setThemeState((t) => (t === "dark" ? "light" : "dark"));

  const { data: me, isLoading: meLoading, isFetched: meFetched, refetch } = useQuery({
    queryKey: ["me"],
    queryFn: () => get("/me"),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const login = useCallback(async (email, password) => {
    await post("/auth/login", { email, password });
    await refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    try { await post("/auth/logout", {}); } catch { /* session may already be gone */ }
    qc.setQueryData(["me"], undefined);
    qc.clear();
    resetDayStatus();
  }, [qc]);

  const impersonate = useCallback(async (userId) => {
    await post("/auth/impersonate", { user_id: userId });
    qc.clear();
    resetDayStatus();
    await refetch();
  }, [qc, refetch]);

  // RBAC: module-wise
  const can = useCallback((module, action = "view") => {
    if (!me?.role) return false;
    const perms = me.role.permissions?.[module];
    return Array.isArray(perms) && perms.includes(action);
  }, [me]);

  // RBAC: project-wise
  const canProject = useCallback((project) => {
    if (!me?.role) return false;
    if (me.role.project_scope === "all") return true;
    return (project?.members || []).some((m) => m.user_id === me.id);
  }, [me]);

  const isAuthenticated = !!me?.id;

  const value = {
    theme, toggleTheme, me, can, canProject,
    login, logout, impersonate, refetchMe: refetch,
    isAuthenticated, authChecked: meFetched, authLoading: meLoading,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
